import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { Survey, SurveyEntry, SurveyEntryInputType, SurveyEntryType, SurveyResult } from 'src/app/services/jsonrpc/cms-server.service';
import { CmsService } from 'src/app/services/cms.service';

interface InputType {
  type: SurveyEntryType;
  input: SurveyEntryInputType;
  display?: string;
}

@Component({
  selector: 'app-survey-result',
  templateUrl: './result.component.html',
  styleUrls: ['./result.component.scss']
})
export class SurveyResultComponent implements OnInit {
  public types: InputType[] = [
    { type: SurveyEntryType.Header, input: null, display: 'Header (no user input)' },
    { type: SurveyEntryType.Input, input: SurveyEntryInputType.Text, display: 'Free Text' },
    { type: SurveyEntryType.Input, input: SurveyEntryInputType.Number, display: 'Number' },
    { type: SurveyEntryType.Input, input: SurveyEntryInputType.Scale, display: 'Choices' }
  ];

  public survey: Survey;
  public loading = true;
  public exists = false;
  public selected = 0;
  public total = 0;

  private offset = 0;
  private limit = 100;
  private results: SurveyResult[];

  public get entries(): SurveyEntry[] {
    return this.survey?.setup?.entries;
  }

  constructor(private router: Router, private route: ActivatedRoute, private cms: CmsService) {}

  public ngOnInit() {
    this.loading = true;
    this.route.paramMap.subscribe(map => {
      const uuid = map.get('uuid');
      return this.cms.cms
        .surveyGet(uuid)
        .then(s => {
          this.survey = s;
          this.total = s.evaluations;
          this.exists = s?.uuid != null;
          this.loading = false;
        })
        .then(s => {
          if (this.exists) {
            return this.select(0);
          }
        });
    });
  }

  public async select(index: number) {
    if (this.results == null || index < this.offset || index > this.offset + this.results.length) {
      this.loading = true;
      const newOff = Math.floor(index / this.limit) * this.limit;
      await this.cms.cms
        .surveyEvaluations({
          survey: this.survey.uuid,
          offset: newOff,
          limit: this.limit
        })
        .then(res => {
          this.results = res;
          this.offset = newOff;
          this.selected = index;
          this.loading = false;
        });
    } else {
      this.selected = index;
    }
  }

  public back() {
    return this.router.navigate(['/survey']);
  }

  private selectorNeighborCount = 3;

  private range(start: number, end: number): number[] {
    if (start >= end) {
      return [];
    } else {
      return Array.from({ length: end - start }, (v, k) => k + start);
    }
  }

  public get selectorLeft(): number[] {
    const start = Math.max(0, this.selected - this.selectorNeighborCount);
    return this.range(start, this.selected);
  }

  public get selectorRight(): number[] {
    const end = Math.min(this.total, this.selected + this.selectorNeighborCount + 1);
    return this.range(this.selected + 1, end);
  }

  public get selectorShowFirstEntry(): boolean {
    return this.selected > this.selectorNeighborCount;
  }

  public get selectorShowLastEntry(): boolean {
    return this.selected < this.total - this.selectorNeighborCount - 1;
  }

  public get selectorShowFirstNav(): boolean {
    return this.selected >= 1;
  }

  public get selectorShowLastNav(): boolean {
    return this.selected < this.total - 1;
  }

  public get result(): SurveyResult {
    if (this.results == null) {
      return null;
    }
    const index = this.selected - this.offset;
    if (index < 0 || index >= this.results.length) {
      return null;
    }
    return this.results[index];
  }

  public get resultDateFmt(): string {
    return this.parseDate(this.result?.timestamp)?.toLocaleString();
  }

  private parseDate(param: number | string | Date): Date {
    if (param == null || param === '') {
      return null;
    } else if (param instanceof Date) {
      return param;
    } else if (typeof param === 'number') {
      return new Date(+param * 1000);
    } else {
      return new Date(param);
    }
  }
}
